import { Fragment, type FC } from 'react';
import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  Calculate,
  CheckCircleOutlined,
  ChecklistRtl,
  CloudUpload,
  Construction,
  EnergySavingsLeaf,
  Light,
  LocationCity,
  RollerShades,
  Shower,
  WarningAmber,
} from '@mui/icons-material';
import { NavLink } from 'react-router';

import { type AuditFormType } from '#packages/data-collection/models/audit-form';
import { type FormIssue } from '#packages/data-collection/models/audit-form-review';

const FORM_ICONS: Record<AuditFormType, React.ElementType> = {
  background: LocationCity,
  mechanical: Construction,
  'lighting-type': Light,
  'lighting-count': Calculate,
  'water-fixture': Shower,
  windows: RollerShades,
  checklist: ChecklistRtl,
  ecms: EnergySavingsLeaf,
};

export type FormReviewEntry = {
  formType: AuditFormType;
  label: string;
  link: string;
  issues: FormIssue[];
};

export type AuditReviewSubmitPageRendererProps = {
  formEntries: FormReviewEntry[];
  onUpload: () => void;
};

export const AuditReviewSubmitPageRenderer: FC<
  AuditReviewSubmitPageRendererProps
> = ({ formEntries, onUpload }) => {
  const hasIssues = formEntries.some((entry) => entry.issues.length > 0);

  return (
    <Fragment>
      <Stack>
        {hasIssues && (
          <Alert severity="warning" sx={{ m: 0.5 }}>
            Some forms have missing required information. Review the issues
            below before uploading.
          </Alert>
        )}
        <List sx={{ flexGrow: 1 }}>
          {formEntries.map((entry) => {
            const hasEntryIssues = entry.issues.length > 0;
            const secondaryText = hasEntryIssues
              ? entry.issues
                  .map((issue) => `${issue.field}: ${issue.message}`)
                  .join(' · ')
              : 'All required fields completed';
            const IconComponent = FORM_ICONS[entry.formType];

            return (
              <ListItem
                key={entry.formType}
                divider
                secondaryAction={
                  hasEntryIssues ? (
                    <WarningAmber color="warning" />
                  ) : (
                    <CheckCircleOutlined color="success" />
                  )
                }>
                <ListItemButton component={NavLink} to={entry.link}>
                  <ListItemIcon>
                    <IconComponent />
                  </ListItemIcon>
                  <ListItemText
                    primary={entry.label}
                    secondary={secondaryText}
                    slotProps={{
                      secondary: {
                        sx: {
                          color: hasEntryIssues
                            ? 'warning.main'
                            : 'text.secondary',
                        },
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<CloudUpload />}
            onClick={onUpload}>
            Upload to Server
          </Button>
        </Box>
      </Stack>
    </Fragment>
  );
};
